import React, { useState, useMemo, useCallback } from 'react';
import {
  TableBody,
  TableHead,
  TableRow,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';

// Sort types
type SortDirection = 'ASC' | 'DESC';
interface SortConfig {
  column: string;
  direction: SortDirection;
}

export interface ItemsKgTableProps {
  data: any[];
  isLoading?: boolean;
  moduleName?: string;
  unexEnabled?: boolean;
  rowBackgroundColor?: string;
}

// Styled components
const StyledTableContainer = styled('div')(() => ({
  maxHeight: '500px',
  overflowX: 'auto',
  overflowY: 'auto',
  border: '1px solid #E1E8ED',
  borderRadius: '8px',
  marginBottom: '24px',
  position: 'relative',
  isolation: 'isolate',
  // Custom scrollbar styling for better visibility
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
    '&:hover': {
      background: '#555',
    },
  },
  // For Firefox
  scrollbarWidth: 'thin',
  scrollbarColor: '#888 #f1f1f1',
}));

const StyledPaper = styled('div')(() => ({
  width: '100%',
  backgroundColor: '#FFFFFF',
}));

const StyledTable = styled('table')(() => ({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: '1px',
  backgroundColor: '#F5F5F5',
}));

const TableWrapper = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
}));

const HeaderCell = styled('th')<{ sortable?: boolean }>(({ theme, sortable }) => ({
  fontWeight: 700,
  fontSize: '12px',
  backgroundColor: '#E7EFF5 !important',
  textAlign: 'center',
  color: '#063142',
  padding: theme.spacing(1, 1.5),
  position: 'sticky',
  top: 0,
  zIndex: 1300,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  border: 'none',
  minWidth: '80px',
  cursor: sortable ? 'pointer' : 'default',
  userSelect: 'none',
  overflow: 'visible',
  '&:hover': sortable ? {
    backgroundColor: '#D5E3ED !important',
  } : {},
}));

const DataCell = styled('td')<{ customBgColor?: string }>(({ theme, customBgColor }) => ({
  fontSize: '11px',
  textAlign: 'center',
  backgroundColor: customBgColor || '#FFFFFF',
  border: 'none',
  padding: theme.spacing(0.75, 1.5),
  '&.number-cell': {
    fontFamily: 'monospace',
    textAlign: 'right',
    fontWeight: 500,
  },
  '&.total-row': {
    backgroundColor: '#F8F9FA',
    fontWeight: 700,
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '18px',
  color: '#063142',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(3),
  '&:first-of-type': {
    marginTop: 0,
  },
}));

/**
 * ItemsKgTable component to display Items-kg report data
 */
function ItemsKgTable({
  data = [],
  isLoading = false,
  moduleName,
  unexEnabled = false,
  rowBackgroundColor,
  ...props
}: ItemsKgTableProps): React.ReactElement {
  // Sort state
  const [sortConfig, setSortConfig] = useState<SortConfig | undefined>(undefined);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null);
  const [activeColumn, setActiveColumn] = useState<string | null>(null);

  // Helper function to parse numeric values (same as NO2Report)
  function parseNumeric(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[\'`,\s]/g, '');
      const num = Number(cleaned);
      if (Number.isFinite(num)) return num;
    }
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <TableWrapper data-testid="items-kg-table-loading">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CircularProgress size={40} />
          <Typography sx={{ mt: 2, color: '#666' }}>
            Loading data...
          </Typography>
        </Box>
      </TableWrapper>
    );
  }

  // Show empty state
  if (!data || data.length === 0) {
    return (
      <TableWrapper data-testid="items-kg-table-empty">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <Typography sx={{ color: '#666', fontStyle: 'italic' }}>
            No data available
          </Typography>
        </Box>
      </TableWrapper>
    );
  }

  // Extract data from the response structure
  const responseData = Array.isArray(data) ? data[0] : data;
  const contentData = responseData?.content || [];
  const totalData = responseData?.total;
  const statusTableData = responseData?.statusTable || [];

  // Sort the content data (same logic as NO2Report)
  const sortedContentData = useMemo(() => {
    if (!contentData || contentData.length === 0) return contentData;
    if (!sortConfig) return contentData;

    const { column, direction } = sortConfig;
    const multiplier = direction === 'ASC' ? 1 : -1;
    
    return [...contentData].sort((a: any, b: any) => {
      const av = a?.[column];
      const bv = b?.[column];
      const an = parseNumeric(av);
      const bn = parseNumeric(bv);

      if (av == null && bv == null) return 0;
      if (av == null) return -1 * multiplier;
      if (bv == null) return 1 * multiplier;

      if (an !== null && bn !== null) return (an - bn) * multiplier;

      const astr = String(av);
      const bstr = String(bv);
      return astr.localeCompare(bstr, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
    });
  }, [contentData, sortConfig]);

  // Sort handlers
  const handleSortClick = useCallback((event: React.MouseEvent<HTMLElement>, column: string) => {
    setActiveColumn(column);
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorPosition({
      top: rect.bottom,
      left: rect.left,
    });
    setSortAnchorEl(event.currentTarget);
  }, []);

  const handleSortSelect = useCallback((direction: SortDirection) => {
    if (activeColumn) {
      // Check if clicking the same sort direction - if so, disable sorting
      if (sortConfig && sortConfig.column === activeColumn && sortConfig.direction === direction) {
        setSortConfig(undefined);
      } else {
        setSortConfig({ column: activeColumn, direction });
      }
    }
    setSortAnchorEl(null);
    setAnchorPosition(null);
  }, [activeColumn, sortConfig]);

  const handleCloseSortMenu = useCallback(() => {
    setSortAnchorEl(null);
    setAnchorPosition(null);
  }, []);

  // Helper function to format numbers
  const formatNumber = (value: any, decimals: number = 0): string => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (isNaN(num)) return String(value);
    
    // Format to specified decimal places
    return decimals > 0 ? Number(num).toFixed(decimals) : Math.round(num).toString();
  };

  // Sortable Header Cell Component
  const SortableHeaderCell = ({ 
    column, 
    label, 
    sortable = true 
  }: { 
    column: string; 
    label: string; 
    sortable?: boolean;
  }) => (
    <HeaderCell sortable={sortable}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            textAlign: 'center',
            marginRight: 2,
            flex: 1,
          }}
        >
          {label}
        </Typography>
        {sortable && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleSortClick(e, column);
            }}
            sx={{
              position: 'absolute',
              right: 0,
              padding: '2px',
              backgroundColor: sortConfig && sortConfig.column === column
                ? '#E3F2FD !important'
                : '#E7EFF5 !important',
              border: sortConfig && sortConfig.column === column
                ? '1px solid #2196F3'
                : 'none',
              '&:hover': {
                backgroundColor: sortConfig && sortConfig.column === column
                  ? '#FFEBEE !important'
                  : '#E3F2FD !important',
              },
              zIndex: 1301,
            }}
          >
            {sortConfig && sortConfig.column === column ? (
              sortConfig.direction === 'ASC' ? (
                <ArrowUpwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
              ) : (
                <ArrowDownwardIcon sx={{ fontSize: 12, color: '#1976D2' }} />
              )
            ) : (
              <MoreVertIcon sx={{ fontSize: 12 }} />
            )}
          </IconButton>
        )}
      </Box>
    </HeaderCell>
  );

  // Status Table Component
  const StatusTable = () => (
    <>
      <SectionTitle>File Status</SectionTitle>
      <Box sx={{ maxWidth: '900px', width: '100%' }}>
        <StyledTableContainer>
          <StyledPaper>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <HeaderCell>eNO1 Filename</HeaderCell>
                  <HeaderCell>eNO1 Status</HeaderCell>
                  <HeaderCell>e55 Filename</HeaderCell>
                  <HeaderCell>e55 Status</HeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statusTableData.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <DataCell>{row.eno1FileName || ''}</DataCell>
                    <DataCell>{row.eno1Status || ''}</DataCell>
                    <DataCell>{row.e55FileName || ''}</DataCell>
                    <DataCell>{row.e55Status || ''}</DataCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </StyledPaper>
        </StyledTableContainer>
      </Box>
    </>
  );

  // UNEX Simplified Table Component
  const UnexTable = () => (
    <>
      <SectionTitle>Items-kg Report (UNEX View)</SectionTitle>
      <StyledTableContainer>
        <StyledPaper>
          <StyledTable>
            <TableHead>
              <TableRow>
                <SortableHeaderCell column="sendingDo" label="Sending DO" />
                <SortableHeaderCell column="receivingDo" label="Receiving DO" />
                <HeaderCell>From Quarter</HeaderCell>
                <HeaderCell>To Quarter</HeaderCell>
                <SortableHeaderCell column="pnetUnex" label="P net for UNEX" />
                <SortableHeaderCell column="pitemsUnex" label="P items for UNEX" />
                <SortableHeaderCell column="gnetUnex" label="G net for UNEX" />
                <SortableHeaderCell column="gitemsUnex" label="G items for UNEX" />
                <SortableHeaderCell column="enetUnex" label="E net for UNEX" />
                <SortableHeaderCell column="eitemsUnex" label="E items for UNEX" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedContentData.map((row: any, index: number) => (
                <TableRow key={index}>
                  <DataCell customBgColor={rowBackgroundColor}>{row.sendingDo || ''}</DataCell>
                  <DataCell customBgColor={rowBackgroundColor}>{row.receivingDo || ''}</DataCell>
                  <DataCell customBgColor={rowBackgroundColor}>{`${row.accYear || ''} Q${row.quarter || ''}`}</DataCell>
                  <DataCell customBgColor={rowBackgroundColor}>{`${row.accYear || ''} Q${row.quarter || ''}`}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.pnetUnex, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.pitemsUnex, 0)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.gnetUnex, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.gitemsUnex, 0)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.enetUnex, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.eitemsUnex, 0)}</DataCell>
                </TableRow>
              ))}
              {totalData && (
                <TableRow>
                  <DataCell className="total-row">TOTAL</DataCell>
                  <DataCell className="total-row"></DataCell>
                  <DataCell className="total-row"></DataCell>
                  <DataCell className="total-row"></DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.pnetUnex, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.pitemsUnex, 0)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.gnetUnex, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.gitemsUnex, 0)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.enetUnex, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.eitemsUnex, 0)}</DataCell>
                </TableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledPaper>
      </StyledTableContainer>
    </>
  );

  // Full Table Component
  const FullTable = () => (
    <>
      <SectionTitle>Items-kg Report (Full View)</SectionTitle>
      <StyledTableContainer>
        <StyledPaper>
          <StyledTable>
            <TableHead>
              <TableRow>
                <SortableHeaderCell column="sendingDo" label="Sending DO" />
                <SortableHeaderCell column="receivingDo" label="Receiving DO" />
                <SortableHeaderCell column="accYear" label="Acc Year" />
                <SortableHeaderCell column="quarter" label="Quarter" />
                <SortableHeaderCell column="sendingOe" label="Sending OE" />
                <SortableHeaderCell column="receivingOe" label="Receiving OE" />
                <SortableHeaderCell column="mc" label="MC" />
                <SortableHeaderCell column="remunGroup" label="Remun Group" />
                <SortableHeaderCell column="excludeForUnexYn" label="ETOE y/n" />
                <SortableHeaderCell column="unexRelevant" label="UNEX relevant" />
                <SortableHeaderCell column="grossP" label="Gross P" />
                <SortableHeaderCell column="netP" label="Net P" />
                <SortableHeaderCell column="itemsP" label="Items P" />
                <SortableHeaderCell column="grossG" label="Gross G" />
                <SortableHeaderCell column="netG" label="Net G" />
                <SortableHeaderCell column="itemsG" label="Items G" />
                <SortableHeaderCell column="grossESampledFlow" label="Gross E sampled flow" />
                <SortableHeaderCell column="netESampledFlow" label="Net E sampled flow" />
                <SortableHeaderCell column="itemsESampledFlow" label="Items E sampled flow" />
                <SortableHeaderCell column="grossENonSampledFlow" label="Gross E non-sampled flow" />
                <SortableHeaderCell column="netENonSampledFlow" label="Net E non-sampled flow" />
                <SortableHeaderCell column="itemsENonSampledFlow" label="Items E non-sampled flow" />
                <SortableHeaderCell column="grossETotal" label="Gross E total" />
                <SortableHeaderCell column="netETotal" label="Net E total" />
                <SortableHeaderCell column="itemsETotal" label="Items E total" />
                <SortableHeaderCell column="ritems" label="R items" />
                <SortableHeaderCell column="vitems" label="V items" />
                <SortableHeaderCell column="expressItems" label="Express items" />
                <SortableHeaderCell column="rvexItemsSampled" label="RVEx items sampled" />
                <SortableHeaderCell column="rvexNetSampled" label="RVEx net sampled" />
                <SortableHeaderCell column="pnetUnex" label="P net for UNEX" />
                <SortableHeaderCell column="pitemsUnex" label="P items for UNEX" />
                <SortableHeaderCell column="gnetUnex" label="G net for UNEX" />
                <SortableHeaderCell column="gitemsUnex" label="G items for UNEX" />
                <SortableHeaderCell column="enetUnex" label="E net for UNEX" />
                <SortableHeaderCell column="eitemsUnex" label="E items for UNEX" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedContentData.map((row: any, index: number) => (
                <TableRow key={index}>
                  <DataCell customBgColor={rowBackgroundColor}>{row.sendingDo || ''}</DataCell>
                  <DataCell customBgColor={rowBackgroundColor}>{row.receivingDo || ''}</DataCell>
                  <DataCell customBgColor={rowBackgroundColor}>{row.accYear || ''}</DataCell>
                  <DataCell customBgColor={rowBackgroundColor}>{row.quarter || ''}</DataCell>
                  <DataCell customBgColor={rowBackgroundColor}>{row.sendingOe || ''}</DataCell>
                  <DataCell customBgColor={rowBackgroundColor}>{row.receivingOe || ''}</DataCell>
                  <DataCell customBgColor={rowBackgroundColor}>{row.mc || ''}</DataCell>
                  <DataCell customBgColor={rowBackgroundColor}>{row.remunGroup || ''}</DataCell>
                  <DataCell customBgColor={rowBackgroundColor}>{row.excludeForUnexYn || ''}</DataCell>
                  <DataCell customBgColor={rowBackgroundColor}>{row.unexRelevant || ''}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.grossP, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.netP, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.itemsP, 0)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.grossG, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.netG, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.itemsG, 0)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.grossESampledFlow, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.netESampledFlow, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.itemsESampledFlow, 0)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.grossENonSampledFlow, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.netENonSampledFlow, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.itemsENonSampledFlow, 0)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.grossETotal, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.netETotal, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.itemsETotal, 0)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.ritems, 0)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.vitems, 0)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.expressItems, 0)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.rvexItemsSampled, 0)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.rvexNetSampled, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.pnetUnex, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.pitemsUnex, 0)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.gnetUnex, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.gitemsUnex, 0)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.enetUnex, 1)}</DataCell>
                  <DataCell className="number-cell" customBgColor={rowBackgroundColor}>{formatNumber(row.eitemsUnex, 0)}</DataCell>
                </TableRow>
              ))}
              {totalData && (
                <TableRow>
                  <DataCell className="total-row">TOTAL</DataCell>
                  <DataCell className="total-row"></DataCell>
                  <DataCell className="total-row"></DataCell>
                  <DataCell className="total-row"></DataCell>
                  <DataCell className="total-row"></DataCell>
                  <DataCell className="total-row"></DataCell>
                  <DataCell className="total-row"></DataCell>
                  <DataCell className="total-row"></DataCell>
                  <DataCell className="total-row"></DataCell>
                  <DataCell className="total-row"></DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.grossP, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.netP, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.itemsP, 0)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.grossG, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.netG, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.itemsG, 0)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.grossESampledFlow, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.netESampledFlow, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.itemsESampledFlow, 0)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.grossENonSampledFlow, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.netENonSampledFlow, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.itemsENonSampledFlow, 0)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.grossETotal, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.netETotal, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.itemsETotal, 0)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.ritems, 0)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.vitems, 0)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.expressItems, 0)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.rvexItemsSampled, 0)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.rvexNetSampled, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.pnetUnex, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.pitemsUnex, 0)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.gnetUnex, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.gitemsUnex, 0)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.enetUnex, 1)}</DataCell>
                  <DataCell className="number-cell total-row">{formatNumber(totalData.eitemsUnex, 0)}</DataCell>
                </TableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledPaper>
      </StyledTableContainer>
    </>
  );

  return (
    <TableWrapper data-testid="items-kg-table-wrapper">
      {/* Status Table - Always show if available */}
      {statusTableData.length > 0 && <StatusTable />}
      
      {/* Main Data Table - UNEX or Full view */}
      {contentData.length > 0 && (
        unexEnabled ? <UnexTable /> : <FullTable />
      )}

      {/* Sort Menu */}
      <Menu
        open={Boolean(anchorPosition)}
        onClose={handleCloseSortMenu}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition || undefined}
        MenuListProps={{
          style: {
            padding: '4px 0',
          },
        }}
        PaperProps={{
          style: {
            zIndex: 1500,
          },
        }}
      >
        <MenuItem onClick={() => handleSortSelect('ASC')}>
          <ArrowUpwardIcon sx={{ mr: 1, fontSize: 16 }} />
          Sort Ascending
        </MenuItem>
        <MenuItem onClick={() => handleSortSelect('DESC')}>
          <ArrowDownwardIcon sx={{ mr: 1, fontSize: 16 }} />
          Sort Descending
        </MenuItem>
        {sortConfig && sortConfig.column === activeColumn && (
          <MenuItem 
            onClick={() => handleSortSelect(sortConfig.direction)}
            sx={{ color: '#F44336' }}
          >
            <CloseIcon sx={{ mr: 1, fontSize: 16 }} />
            Clear Sort
          </MenuItem>
        )}
      </Menu>
    </TableWrapper>
  );
}

export default ItemsKgTable; 